import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Spin, notification } from 'antd';
import {
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  RobotOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { formatTime } from '../../utils/helpers';
import { ThemeModal, ThemeButton } from '../Styles/Theme';
import {
  TranscriptModalContent,
  TranscriptMessage,
  TranscriptSender,
  TranscriptText,
  TranscriptTimestamp,
  CallInfoHeader,
  CallInfoItem,
  CallInfoLabel,
  CallInfoValue,
  EmptyTranscriptState,
} from './CampaignCallsStyle';
import {
  getCallTranscriptApi,
  getCallTranscriptReset,
} from '../../store/actions/campaignAction';

// Helper to format duration
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const TranscriptViewer = (props) => {
  const {
    visible,
    onClose,
    campaignId,
    callId,
    callInfo,
    canDownload,
    onStarToggle,
    toggleCallStarState,
    getCallTranscript,
    getCallTranscriptReset,
    getCallTranscriptState,
  } = props;

  const [transcript, setTranscript] = useState([]);
  const [transcriptMeta, setTranscriptMeta] = useState(null);
  const [fetchingContent, setFetchingContent] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Fetch transcript when modal opens
  useEffect(() => {
    if (visible && campaignId && callId) {
      getCallTranscript(campaignId, callId, false);
    }
    return () => {
      if (!visible) {
        getCallTranscriptReset();
        setTranscript([]);
        setTranscriptMeta(null);
      }
    };
  }, [visible, campaignId, callId]);

  // Helper to parse transcript data from various formats
  const parseTranscriptData = (data) => {
    if (data.transcript) {
      // Parse transcript - could be array of messages or string
      if (Array.isArray(data.transcript)) {
        return data.transcript;
      } else if (typeof data.transcript === 'string') {
        try {
          const parsed = JSON.parse(data.transcript);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [{ role: 'system', content: data.transcript }];
        }
      }
    }
    return [];
  };

  // Fetch transcript content from Azure URL
  const fetchTranscriptFromUrl = async (url) => {
    setFetchingContent(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch transcript content');
      }
      const data = await response.json();
      const parsedTranscript = parseTranscriptData(data);
      setTranscript(parsedTranscript);
      setTranscriptMeta({
        // Support both new format (participantId, duration, callDate) and legacy format
        callId: data.participantId || data.respondent_id || callId,
        duration: data.duration || data.call_duration,
        callDate: data.callDate || data.call_completed_at,
        status: 'uploaded',
        analysis: data.analysis,
        demographics: data.demographics || null,
      });
    } catch (error) {
      console.error('Error fetching transcript content:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load transcript content',
      });
      setTranscript([]);
    } finally {
      setFetchingContent(false);
    }
  };

  // Handle transcript fetch response
  useEffect(() => {
    if (getCallTranscriptState.apiState === 'success' && getCallTranscriptState.data) {
      const transcriptData = getCallTranscriptState.data.data || getCallTranscriptState.data;

      // Check if response contains a URL to fetch transcript from
      if (transcriptData.status === 'ready' && transcriptData.url) {
        // Fetch actual transcript content from the URL
        fetchTranscriptFromUrl(transcriptData.url);
        getCallTranscriptReset();
        return;
      }

      // Direct transcript data (legacy format)
      if (transcriptData.transcript) {
        const parsedTranscript = parseTranscriptData(transcriptData);
        setTranscript(parsedTranscript);
      } else {
        setTranscript([]);
      }

      setTranscriptMeta({
        callId: transcriptData.call_id || callId,
        duration: transcriptData.duration,
        callDate: transcriptData.call_date,
        status: transcriptData.transcript_status,
      });

      getCallTranscriptReset();
    } else if (getCallTranscriptState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCallTranscriptState.message || 'Failed to fetch transcript',
      });
      getCallTranscriptReset();
    }
  }, [getCallTranscriptState, getCallTranscriptReset, callId]);

  const isLoading = getCallTranscriptState.apiState === 'loading' || fetchingContent;

  const generateTranscriptContent = () => {
    const lines = [];

    // Header section
    lines.push('='.repeat(60));
    lines.push('TRANSCRIPT');
    lines.push('='.repeat(60));
    lines.push('');

    // Participant Info
    if (callInfo?.participantId) {
      lines.push(`Participant ID: ${callInfo.participantId}`);
    }
    if (callInfo?.callDate) {
      lines.push(`Call Date: ${callInfo.callDate}`);
    }
    if (callInfo?.duration != null) {
      lines.push(`Duration: ${formatDuration(callInfo.duration)}`);
    }
    // Demographics section
    if (transcriptMeta?.demographics && Object.keys(transcriptMeta.demographics).length > 0) {
      lines.push('');
      lines.push('-'.repeat(40));
      lines.push('DEMOGRAPHICS');
      lines.push('-'.repeat(40));

      const d = transcriptMeta.demographics;
      if (d.gender) lines.push(`Gender: ${d.gender}`);
      if (d.age != null) lines.push(`Age: ${d.age}`);
      if (d.race_ethnicity) lines.push(`Race/Ethnicity: ${d.race_ethnicity}`);
      if (d.hispanic_origin) lines.push(`Hispanic Origin: ${d.hispanic_origin}`);
      if (d.state) lines.push(`State: ${d.state}`);
      if (d.region) lines.push(`Region: ${d.region}`);
      if (d.zipcode) lines.push(`Zipcode: ${d.zipcode}`);
    }

    // Transcript section
    if (transcript && transcript.length > 0) {
      lines.push('');
      lines.push('-'.repeat(40));
      lines.push('CONVERSATION');
      lines.push('-'.repeat(40));
      lines.push('');

      transcript.forEach((msg) => {
        const role = msg.role === 'assistant' || msg.role === 'agent' || msg.role === 'ai' ? 'Agent' : 'Respondent';
        lines.push(`[${role}]: ${msg.content || msg.text || msg.message}`);
        lines.push('');
      });
    }

    lines.push('='.repeat(60));
    lines.push('END OF TRANSCRIPT');
    lines.push('='.repeat(60));

    return lines.join('\n');
  };

  const handleDownload = () => {
    if (!transcript.length) {
      notification.warning({ message: 'No transcript to download' });
      return;
    }

    setDownloading(true);
    try {
      const textContent = generateTranscriptContent();
      const filename = `transcript_${callInfo?.participantId || callId}.txt`;

      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notification.success({ message: 'Transcript downloaded successfully' });
    } catch (error) {
      console.error('Download error:', error);
      notification.error({ message: 'Failed to download transcript' });
    } finally {
      setDownloading(false);
    }
  };

  const renderTranscriptMessage = (message, index) => {
    const isAgent = message.role === 'assistant' || message.role === 'agent' || message.role === 'ai';
    const senderName = isAgent ? 'AI Agent' : 'Participant';
    const senderIcon = isAgent ? <RobotOutlined /> : <UserOutlined />;

    return (
      <TranscriptMessage key={index} $isAgent={isAgent}>
        <TranscriptSender $isAgent={isAgent}>
          {senderIcon} {senderName}
        </TranscriptSender>
        <TranscriptText>{message.content || message.text || message.message}</TranscriptText>
        {message.timestamp && (
          <TranscriptTimestamp>
            {formatTime(message.timestamp)}
          </TranscriptTimestamp>
        )}
      </TranscriptMessage>
    );
  };

  return (
    <ThemeModal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileTextOutlined />
          <span>Call Transcript</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      className="transcript-modal"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {onStarToggle && (
              <ThemeButton
                className="noBackground"
                icon={callInfo?.isStarred ? <StarFilled style={{ color: '#FAAD14' }} /> : <StarOutlined />}
                onClick={() => {
                  const isToggling = toggleCallStarState?.apiState === 'loading' && toggleCallStarState?.callId === callId;
                  if (!isToggling) onStarToggle();
                }}
                loading={toggleCallStarState?.apiState === 'loading' && toggleCallStarState?.callId === callId}
              >
                {callInfo?.isStarred ? 'Remove Star' : 'Mark as Starred'}
              </ThemeButton>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canDownload && transcript.length > 0 && (
              <ThemeButton loading={downloading} onClick={handleDownload}>
                Download
              </ThemeButton>
            )}
            <ThemeButton onClick={onClose}>
              Close
            </ThemeButton>
          </div>
        </div>
      }
    >
      <Spin spinning={isLoading} tip="Loading transcript...">
        {/* Call Info Header */}
        <CallInfoHeader>
          <CallInfoItem>
            <CallInfoLabel>Participant ID</CallInfoLabel>
            <CallInfoValue>{callInfo?.participantId || '-'}</CallInfoValue>
          </CallInfoItem>
          <CallInfoItem>
            <CallInfoLabel>Call Date</CallInfoLabel>
            <CallInfoValue>{callInfo?.callDate || '-'}</CallInfoValue>
          </CallInfoItem>
          <CallInfoItem>
            <CallInfoLabel>Duration</CallInfoLabel>
            <CallInfoValue>
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {formatDuration(callInfo?.duration)}
            </CallInfoValue>
          </CallInfoItem>
        </CallInfoHeader>

        {/* Transcript Content */}
        <TranscriptModalContent>
          {transcript.length > 0 ? (
            transcript.map((message, index) => renderTranscriptMessage(message, index))
          ) : (
            <EmptyTranscriptState>
              <FileTextOutlined />
              <h4>No Transcript Available</h4>
              <p>
                {isLoading
                  ? 'Loading transcript...'
                  : 'The transcript for this call is not yet available or failed to load.'}
              </p>
            </EmptyTranscriptState>
          )}
        </TranscriptModalContent>
      </Spin>
    </ThemeModal>
  );
};

const mapStateToProps = (state) => ({
  getCallTranscriptState: state.campaign.getCallTranscriptState,
});

const mapDispatchToProps = (dispatch) => ({
  getCallTranscript: (campaignId, callId, trigger) =>
    dispatch(getCallTranscriptApi(campaignId, callId, trigger)),
  getCallTranscriptReset: () => dispatch(getCallTranscriptReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TranscriptViewer);
